/**
 * promise-based confirm dialog with configurable action buttons
 */

import React from "react";
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Box,
} from "@mui/material";
import Card from 'components/card';
import Icon from 'components/icon';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: theme.spacing(40),
    backgroundColor: 'transparent',
    '& .MuiDialogActions-spacing > :not(:first-child)': {
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0px',
      },
    },
  },
  titleIcon: {
    marginRight: theme.spacing.dialogTitleIconMargin,
  },
  extraButtons: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      padding: theme.spacing(2),
      flexDirection: 'column-reverse',
    },
  },
  actionButton: {
    minWidth: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      minHeight: theme.spacing(6),
    },
  },
}));

// button configuration when customActions prop isn't provided
const defaultActions = [
  {
    label: 'Cancel',
    value: false,
    isNotPriority: true,
    dataTestId: 'confirm-dialog-cancel-button',
  },
  {
    label: 'OK',
    value: true,
    dataTestId: 'confirm-dialog-ok-button',
  },
];

export default function ConfirmDialog(props) {
  const {
    setConfirm,
    customActions,
    title,
    titleStyle,
    titleIcon,
    maxWidth,
    content
  } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [promise, setPromise] = React.useState();
  const [actions, setActions] = React.useState(defaultActions);

  React.useEffect(() => { 
    setActions(customActions || defaultActions);
  }, [customActions]);

  // the setConfirm prop must be a function that will set this component's confirm method.
  // this allows the parent to open the dialog and wait for a response via a promise.
  React.useEffect(() => {
    if (setConfirm) {
      setConfirm(() => {
        return confirm;
      });
    }

  // setConfirm is setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfirm])

  // the confirm method passed to the parent via the setConfirm property.
  // this returns a promise that is resolved when the user chooses a response.
  const confirm = () => {
    return new Promise((resolve, reject) => {
      setPromise({ resolve, reject });
      setOpen(true);
    });
  };

  // dialog button handler
  const handleAction = (action) => {
    promise.resolve(action);
    setOpen(false);
  };

  // close dialog handler
  const close = () => {
    promise.resolve(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      classes={{paper: classes.root}}
      maxWidth={maxWidth}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      data-testid={props['data-testid']}
    >
      <Card
        titleStyle={titleStyle || "dark"}
        title={<>
          <Icon className={classes.titleIcon} type={titleIcon ? titleIcon : 'info'}/>
          {title}
        </>}
        id="confirm-dialog-title"
      >
        {(content && 
          <Box>
            <DialogContent className={classes.content} id="confirm-dialog-description" >              
              {typeof(content) === 'string' 
              ? <DialogContentText style={{marginBottom: 0}}>
                {content}
              </DialogContentText>
              : content}
            </DialogContent>
          </Box>
        )}
        <DialogActions className={actions.length > 2 ? classes.extraButtons : ''}>
          {actions.map((action) => (
            <Button 
              className={classes.actionButton} 
              onClick={() => handleAction(action.value)} 
              color={action.isNotPriority ? "grey" : "primary"}
              key={action.value}
              data-testid={action.dataTestId}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      </Card>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  setConfirm: PropTypes.func.isRequired,  // function to be assigned by dialog that parent will wait on until user chooses a response
  customActions: PropTypes.array,         // overrides default OK/Cancel buttons with array of action button definitions
  title: PropTypes.node.isRequired,       // text or JSX to display in dialog title
  titleStyle: PropTypes.string,           // theme to apply to dialog title header (ex: 'error', 'warning')
  titleIcon: PropTypes.string,            // icon to show in dialog header
  content: PropTypes.node,                // any string or JSX to display in the dialog
};